import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import LoadingIndicator from './LoadingIndicator';

const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requirePermission = null // { module, action, subModule? }
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { can, isAdmin, isLoading: permissionsLoading } = usePermissions();
  const location = useLocation();

  const isLoading = authLoading || permissionsLoading;

  // Mostrar loading mientras verifica la autenticación
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingIndicator
          type="progress"
          progress={75}
          label="Verificando autenticación..."
          size="large"
          color="blue"
          className="w-full max-w-md"
        />
      </div>
    );
  }

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si requiere admin y no es admin, mostrar error de acceso
  if (requireAdmin && !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-auto">
            <div className="text-red-500 text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h1>
            <p className="text-gray-600 mb-6">
              No tienes permisos para acceder a esta sección. Se requieren privilegios de administrador.
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Volver Atrás
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si requiere permisos específicos, verificar
  if (requirePermission) {
    const { module, action, subModule } = requirePermission;
    const hasPermission = can(module, action, subModule);

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-auto">
              <div className="text-amber-500 text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Permiso Denegado</h1>
              <p className="text-gray-600 mb-6">
                No tienes permiso para acceder a esta funcionalidad.
                {module && (
                  <span className="block mt-2 text-sm">
                    Requiere: <strong>{module}</strong> - <strong>{action}</strong>
                    {subModule && ` (${subModule})`}
                  </span>
                )}
              </p>
              <button
                onClick={() => window.history.back()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Volver Atrás
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Si todo está bien, mostrar el componente
  return children;
};

export default ProtectedRoute;